import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCompanies } from '../../hooks/useCompanies';
import { Modal } from '../ui/Modal';
import { PdfUpload } from '../pdf/PdfUpload';
import { CompanyForm } from './CompanyForm';
import { Company } from '../../types';
import { TestBadge } from '../ui/TestBadge';
import { FirmenKategorieBadge, FIRMEN_KATEGORIE_LABELS, FirmenKategorie } from '../ui/FirmenKategorieBadge';
import { apiClient } from '../../api/client';

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz', ES: 'Spanien',
  FR: 'Frankreich', IT: 'Italien', NL: 'Niederlande', BE: 'Belgien',
  GB: 'Vereinigtes Königreich', SE: 'Schweden', DK: 'Dänemark', NO: 'Norwegen',
  PL: 'Polen', PT: 'Portugal', LU: 'Luxemburg', US: 'USA',
};

type SortDir = 'asc' | 'desc';

export function CompanyList() {
  const navigate = useNavigate();
  // Page + Seitengröße in URL persistieren, damit Back-Navigation die Seite behält.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1'));
  const setPage = (next: number | ((p: number) => number)) => {
    const resolved = typeof next === 'function' ? next(page) : next;
    const sp = new URLSearchParams(searchParams);
    if (resolved <= 1) sp.delete('page'); else sp.set('page', String(resolved));
    setSearchParams(sp, { replace: true });
  };
  // Suche ebenfalls in URL persistieren (damit Back-Navigation die Selektion behält)
  const search = searchParams.get('q') ?? '';
  const setSearch = (value: string) => {
    const sp = new URLSearchParams(searchParams);
    if (value) sp.set('q', value); else sp.delete('q');
    sp.delete('page'); // neue Suche → zurück auf Seite 1
    setSearchParams(sp, { replace: true });
  };
  const [sortBy, setSortBy] = useState('display_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterVerified, setFilterVerified] = useState<'all' | 'yes' | 'no'>('all');
  const [filterApproved, setFilterApproved] = useState<'all' | 'yes' | 'no'>('all');
  const [filterTestData, setFilterTestData] = useState<'all' | 'real' | 'test'>('real');
  const [filterKategorie, setFilterKategorie] = useState<'all' | FirmenKategorie>('all');
  const [filterInvestorType, setFilterInvestorType] = useState<string>('all');
  const [filterWebsite, setFilterWebsite] = useState<'all' | 'yes' | 'no'>('all');
  const [filterDoublet, setFilterDoublet] = useState<'all' | 'suspect' | 'with_mom'>('all');
  const [investorTypes, setInvestorTypes] = useState<Array<{ id: string; code: string; label_de: string }>>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => {
    apiClient.get<{ data: Array<{ id: string; code: string; label_de: string }> }>('/investor-types')
      .then(r => setInvestorTypes(r.data.data ?? [])).catch(() => {});
  }, []);
  const limit = (() => {
    const n = parseInt(searchParams.get('limit') ?? '20');
    return [20, 40, 60, 80, 100].includes(n) ? n : 20;
  })();
  const setLimit = (next: number) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 20) sp.delete('limit'); else sp.set('limit', String(next));
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  };

  const { data, isLoading, error, refetch } = useCompanies({
    page, limit,
    search: search || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
    is_verified: filterVerified === 'all' ? undefined : filterVerified === 'yes' ? 'true' : 'false',
    is_approved: filterApproved === 'all' ? undefined : filterApproved === 'yes' ? 'true' : 'false',
    is_test_data: filterTestData === 'all' ? undefined : filterTestData === 'test' ? 'true' : 'false',
    firmen_kategorie: filterKategorie === 'all' ? undefined : filterKategorie,
    investor_type: filterInvestorType === 'all' ? undefined : filterInvestorType,
    has_website: filterWebsite === 'all' ? undefined : filterWebsite === 'yes' ? 'true' : 'false',
    person_doublet: filterDoublet === 'all' ? undefined : filterDoublet,
  });

  const companies: Company[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIcon = (col: string) => {
    if (sortBy !== col) return <span style={{ opacity: 0.3, fontSize: '10px', marginLeft: '4px' }}>↕</span>;
    return <span style={{ fontSize: '10px', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thStyle = (col: string): React.CSSProperties => ({
    padding: '10px 16px', textAlign: 'left', fontWeight: 500,
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    color: sortBy === col ? 'var(--text)' : undefined,
  });

  const thCenterStyle = (col: string): React.CSSProperties => ({
    ...thStyle(col), textAlign: 'center',
  });

  const PAGE_SIZES = [20, 40, 60, 80, 100];

  const toggleSelected = (id: string, ev: React.MouseEvent | React.ChangeEvent) => {
    if ('stopPropagation' in ev) ev.stopPropagation();
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const allOnPageSelected = companies.length > 0 && companies.every(c => selectedIds.has(c.id));
  const togglePageSelection = () => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (allOnPageSelected) {
        for (const c of companies) n.delete(c.id);
      } else {
        for (const c of companies) n.add(c.id);
      }
      return n;
    });
  };

  async function runBulk(action: 'verify' | 'unverify' | 'approve' | 'unapprove' | 'delete') {
    if (selectedIds.size === 0) return;
    const labels: Record<typeof action, string> = {
      verify: 'als geprüft markieren',
      unverify: 'Geprüft-Markierung entfernen',
      approve: 'freigeben',
      unapprove: 'Freigabe entfernen',
      delete: 'löschen (inkl. Kontakte und Profile)',
    } as Record<typeof action, string>;
    const ok = window.confirm(`${selectedIds.size} markierte Investoren ${labels[action]}?`);
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post<{ affected: number }>('/companies/bulk', {
        ids: Array.from(selectedIds),
        action,
      });
      window.alert(`${r.data.affected} Investoren aktualisiert.`);
      setSelectedIds(new Set());
      refetch();
    } catch (err) {
      window.alert(`Fehler: ${(err as Error).message}`);
    } finally {
      setBulkBusy(false);
    }
  }

  const PaginationBar = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text2)', flexWrap: 'wrap', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span>{total.toLocaleString('de-DE')} Investoren gesamt</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
          <select
            value={limit}
            onChange={e => setLimit(parseInt(e.target.value))}
            style={{
              padding: '4px 8px', fontSize: '12px',
              border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
              background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
            }}>
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>pro Seite</span>
        </label>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: page === 1 ? 0.5 : 1 }}>
            ← Zurück
          </button>
          <span style={{ padding: '7px 12px', color: 'var(--text3)' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '6px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: page === totalPages ? 0.5 : 1 }}>
            Weiter →
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Investoren suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: '200px', padding: '7px 12px',
            border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
            fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
          }}
        />
        <button
          onClick={() => setShowPdfModal(true)}
          style={{
            padding: '7px 14px', background: 'var(--surface)', color: 'var(--text2)',
            border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
            fontSize: '13px', cursor: 'pointer',
          }}
        >
          PDF importieren
        </button>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '7px 16px', background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 'var(--radius)', fontSize: '13px',
            fontWeight: 500, cursor: 'pointer',
          }}
        >
          + Neuer Investor
        </button>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Geprüft:</span>
          {(['all', 'yes', 'no'] as const).map(val => (
            <button
              key={val}
              onClick={() => { setFilterVerified(val); setPage(1); }}
              style={{
                padding: '3px 10px', fontSize: '11px', borderRadius: '20px', cursor: 'pointer',
                border: '1px solid var(--border2)',
                background: filterVerified === val ? 'var(--accent)' : 'var(--surface)',
                color: filterVerified === val ? '#fff' : 'var(--text2)',
                fontWeight: filterVerified === val ? 600 : 400,
              }}
            >
              {val === 'all' ? 'Alle' : val === 'yes' ? '✓ Geprüft' : '✗ Nicht geprüft'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Freigegeben:</span>
          {(['all', 'yes', 'no'] as const).map(val => (
            <button
              key={val}
              onClick={() => { setFilterApproved(val); setPage(1); }}
              style={{
                padding: '3px 10px', fontSize: '11px', borderRadius: '20px', cursor: 'pointer',
                border: '1px solid var(--border2)',
                background: filterApproved === val ? 'var(--accent)' : 'var(--surface)',
                color: filterApproved === val ? '#fff' : 'var(--text2)',
                fontWeight: filterApproved === val ? 600 : 400,
              }}
            >
              {val === 'all' ? 'Alle' : val === 'yes' ? '✓ Freigegeben' : '✗ Nicht freigegeben'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Daten:</span>
          {([
            { v: 'real' as const, label: 'Echt' },
            { v: 'test' as const, label: 'Test' },
            { v: 'all'  as const, label: 'Alle' },
          ]).map(opt => (
            <button key={opt.v} onClick={() => { setFilterTestData(opt.v); setPage(1); }}
              style={{
                padding: '3px 10px', fontSize: '11px', borderRadius: '20px', cursor: 'pointer',
                border: '1px solid var(--border2)',
                background: filterTestData === opt.v ? 'var(--accent)' : 'var(--surface)',
                color: filterTestData === opt.v ? '#fff' : 'var(--text2)',
                fontWeight: filterTestData === opt.v ? 600 : 400,
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Kategorie:</span>
          {([
            { v: 'all' as const,            label: 'Alle' },
            { v: 'mit_kontakten' as const,  label: FIRMEN_KATEGORIE_LABELS.mit_kontakten.short },
            { v: 'einzelfirma' as const,    label: FIRMEN_KATEGORIE_LABELS.einzelfirma.short },
            { v: 'ohne_kontakt' as const,   label: FIRMEN_KATEGORIE_LABELS.ohne_kontakt.short },
          ]).map(opt => (
            <button key={opt.v} onClick={() => { setFilterKategorie(opt.v); setPage(1); }}
              style={{
                padding: '3px 10px', fontSize: '11px', borderRadius: '20px', cursor: 'pointer',
                border: '1px solid var(--border2)',
                background: filterKategorie === opt.v ? 'var(--accent)' : 'var(--surface)',
                color: filterKategorie === opt.v ? '#fff' : 'var(--text2)',
                fontWeight: filterKategorie === opt.v ? 600 : 400,
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Investorenart:</span>
          <select value={filterInvestorType} onChange={e => { setFilterInvestorType(e.target.value); setPage(1); }}
            style={{ padding: '3px 8px', fontSize: '11px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)' }}>
            <option value="all">Alle</option>
            <option value="none">— ohne Klassifikation —</option>
            {investorTypes.map(t => <option key={t.code} value={t.code}>{t.label_de}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Webseite:</span>
          <select value={filterWebsite} onChange={e => { setFilterWebsite(e.target.value as 'all' | 'yes' | 'no'); setPage(1); }}
            style={{ padding: '3px 8px', fontSize: '11px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)' }}>
            <option value="all">Alle</option>
            <option value="yes">Mit Webseite</option>
            <option value="no">Ohne Webseite</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text3)' }}>
          <span>Doppelgänger:</span>
          <select value={filterDoublet} onChange={e => { setFilterDoublet(e.target.value as 'all' | 'suspect' | 'with_mom'); setPage(1); }}
            title="Pattern 'Firmenname, Personenname' — vermutlich Personen-Eintrag fälschlich als eigene Firma"
            style={{ padding: '3px 8px', fontSize: '11px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)' }}>
            <option value="all">Alle</option>
            <option value="suspect">Verdächtige (Pattern)</option>
            <option value="with_mom">Mit existierender Mutter-Firma</option>
          </select>
        </div>
      </div>

      {/* Paginierung oben */}
      <div style={{ marginBottom: '12px' }}>{PaginationBar}</div>

      {/* Bulk-Aktionsleiste — nur sichtbar wenn etwas markiert ist */}
      {selectedIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: '12px', borderRadius: 'var(--radius)',
          background: 'var(--accent-light)', border: '1px solid var(--accent)',
          fontSize: '13px',
        }}>
          <span style={{ fontWeight: 600 }}>{selectedIds.size} markiert</span>
          <button onClick={() => runBulk('verify')} disabled={bulkBusy}
            style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid var(--accent)', background: 'var(--surface)', color: 'var(--accent)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
            ✓ Geprüft
          </button>
          <button onClick={() => runBulk('approve')} disabled={bulkBusy}
            style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid var(--accent)', background: 'var(--surface)', color: 'var(--accent)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
            ✓ Freigegeben
          </button>
          <button onClick={() => runBulk('unverify')} disabled={bulkBusy}
            style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
            Geprüft entfernen
          </button>
          <button onClick={() => runBulk('unapprove')} disabled={bulkBusy}
            style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
            Freigabe entfernen
          </button>
          <button onClick={() => runBulk('delete')} disabled={bulkBusy}
            style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid #d32f2f', background: 'var(--surface)', color: '#d32f2f', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
            Löschen
          </button>
          <button onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}
            style={{ marginLeft: 'auto', padding: '5px 12px', fontSize: '12px', border: 'none', background: 'transparent', color: 'var(--text3)', cursor: 'pointer' }}>
            Auswahl leeren
          </button>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--accent)' }}>
            Fehler beim Laden der Firmen.
          </div>
        ) : companies.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>
            Keine Investoren gefunden.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface2)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)' }}>
                <th style={{ padding: '10px 8px 10px 16px', textAlign: 'center', width: '32px' }}>
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={togglePageSelection}
                    onClick={e => e.stopPropagation()}
                  />
                </th>
                <th style={thStyle('display_name')} onClick={() => handleSort('display_name')}>
                  Firmenname{sortIcon('display_name')}
                </th>
                <th style={thStyle('city')} onClick={() => handleSort('city')}>
                  Stadt / Land{sortIcon('city')}
                </th>
                <th style={thStyle('investor_type')} onClick={() => handleSort('investor_type')}>
                  Investorenart{sortIcon('investor_type')}
                </th>
                <th style={thCenterStyle('profile_count')} onClick={() => handleSort('profile_count')}>
                  Profile{sortIcon('profile_count')}
                </th>
                <th style={thCenterStyle('contact_count')} onClick={() => handleSort('contact_count')}>
                  Kontakte{sortIcon('contact_count')}
                </th>
                <th style={thCenterStyle('verified_at')} onClick={() => handleSort('verified_at')}>
                  Geprüft{sortIcon('verified_at')}
                </th>
                <th style={thCenterStyle('approved_at')} onClick={() => handleSort('approved_at')}>
                  Freigegeben{sortIcon('approved_at')}
                </th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company: Company, idx: number) => (
                <tr
                  key={company.id}
                  onClick={() => navigate(`/companies/${company.id}`)}
                  style={{ borderTop: idx > 0 ? '1px solid var(--border)' : undefined, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: '12px 8px 12px 16px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(company.id)}
                      onChange={(e) => toggleSelected(company.id, e)}
                    />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {(() => {
                        const src = company.logo_url ||
                          (company.website ? (() => { try { return `https://www.google.com/s2/favicons?domain=${new URL(company.website!).hostname}&sz=64`; } catch { return null; } })() : null);
                        const initial = (company.display_name || '?').trim().charAt(0).toUpperCase();
                        return (
                          <div style={{
                            width: 20, height: 20, borderRadius: 3, flexShrink: 0,
                            background: 'var(--surface2)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 600, color: 'var(--text3)',
                            position: 'relative', overflow: 'hidden',
                          }}>
                            {initial}
                            {src && (
                              <img src={src} alt=""
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }}
                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                          </div>
                        );
                      })()}
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {company.display_name}
                          <TestBadge show={company.is_test_data} />
                          <FirmenKategorieBadge kategorie={company.firmen_kategorie} />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text2)', fontSize: '12px' }}>
                    {[company.city, company.country_code ? COUNTRY_NAMES[company.country_code] ?? company.country_code : null].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: company.investor_type_label ? 'var(--text2)' : 'var(--text3)' }}>
                    {company.investor_type_label ?? '—'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', minWidth: '28px', padding: '2px 8px',
                      background: (company.profile_count ?? 0) > 0 ? 'var(--accent-light)' : 'var(--surface2)',
                      color: (company.profile_count ?? 0) > 0 ? 'var(--accent)' : 'var(--text3)',
                      borderRadius: '20px', fontSize: '12px', fontWeight: 500, fontFamily: 'DM Mono, monospace',
                    }}>{company.profile_count ?? 0}</span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', minWidth: '28px', padding: '2px 8px',
                      background: 'var(--surface2)', color: 'var(--text2)',
                      borderRadius: '20px', fontSize: '12px', fontFamily: 'DM Mono, monospace',
                    }}>{company.contact_count ?? 0}</span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px' }}>
                    {company.verified_at ? (
                      <span style={{ color: '#2e7d32', fontWeight: 500 }}>
                        ✓ {new Date(company.verified_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text3)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px' }}>
                    {company.approved_at ? (
                      <span style={{ color: '#1565c0', fontWeight: 500 }}>
                        ✓ {new Date(company.approved_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text3)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginierung unten */}
      <div style={{ marginTop: '16px' }}>{PaginationBar}</div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Neuen Investor anlegen" size="md">
        <CompanyForm
          onSuccess={() => { setShowCreateModal(false); refetch(); }}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      <Modal isOpen={showPdfModal} onClose={() => setShowPdfModal(false)} title="PDF importieren" size="md">
        <PdfUpload
          mode="new"
          onSuccess={() => { setShowPdfModal(false); refetch(); }}
          onCancel={() => setShowPdfModal(false)}
        />
      </Modal>
    </div>
  );
}
